import React, { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from '@mui/material/utils';
import TextField from '@mui/material/TextField';

import type { IProperty } from 'src/services/api';
import { useUpdatePropertyNotes } from 'src/services/api';
import ExpandableItem from 'src/components/lead-sidebar/LeadSidebarExpandableItem';

import styles from 'src/components/lead-sidebar/LeadSidebarNotes.module.css';

interface Props {
    property: IProperty;
}

export default function LeadSidebarPropertyNotes({
    property,
}: Props) {

    const { mutateAsync: updateNotes } = useUpdatePropertyNotes(property.id);

    const [notes, setNotes] = useState(property.notes);
    const [defaultExpanded] = useState(!!property.notes);
    const lastSentNotesRef = useRef<string | null>(null);

    const handleSave = useCallback(debounce(async (_notes) => {
        if (property.notes !== _notes) {
            lastSentNotesRef.current = _notes || '';
            await updateNotes(_notes || '');
        }
    }, 1000), [property]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNotes(e.target.value);
    };

    useEffect(() => {
        handleSave(notes);
    }, [notes]);

    // Sync external edits (e.g. from the edit property modal), but ignore the
    // echo of our own autosave so in-flight typing is not clobbered.
    useEffect(() => {
        if (property.notes === lastSentNotesRef.current) {
            lastSentNotesRef.current = null;
            return;
        }
        if (property.notes !== notes) {
            setNotes(property.notes);
        }
    }, [property.notes]);

    return (
        <ExpandableItem title="Notes" defaultExpanded={defaultExpanded}>
            <TextField
                variant="filled"
                placeholder="Write your notes here"
                value={notes ?? ''}
                onChange={handleChange}
                multiline
                rows={5}
                fullWidth
                className={styles.notesField}
                data-testid="property-notes-sidebar-field"
            />
        </ExpandableItem>
    );
}
